import { PeekingBunny } from '@app/components/ui/bunny';
import { SteamingCoffee } from '@app/components/ui/coffee';
import { ProfileDto } from '@app/models/dtos/contentDto';
import { motion, useReducedMotion } from 'framer-motion';
import Image from 'next/image';

export default function Opening({ profile }: { profile: ProfileDto }) {
    const reduced = useReducedMotion();

    const fadeUp = (delay: number) => ({
        initial: { opacity: 0, y: reduced ? 0 : 10 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: reduced ? 0.2 : 0.65, delay: reduced ? 0 : delay, ease: [0.22, 1, 0.36, 1] as const }
    });

    const links = [{ label: profile.email, href: `mailto:${profile.email}`, external: false }, ...profile.socials.map((social) => ({ label: social.name, href: social.link, external: true }))];

    return (
        <section id="opening">
            <motion.h1 className="t-script relative inline-block text-accent" {...fadeUp(0.05)}>
                hello
                <PeekingBunny say="hi!" className="absolute bottom-[0.12em] left-[calc(100%+18px)]" />
            </motion.h1>

            <motion.p className="t-lead mt-7 text-ink-soft" {...fadeUp(0.2)}>
                {/* The cup rides with the last word so it can never orphan onto its own line. */}
                {profile.intro.slice(0, profile.intro.lastIndexOf(' '))}{' '}
                <span className="whitespace-nowrap">
                    {profile.intro.slice(profile.intro.lastIndexOf(' ') + 1)}
                    <SteamingCoffee className="ml-2.5 inline-block align-[-0.32em]" />
                </span>
            </motion.p>

            <motion.div className="mt-10 flex items-start gap-5" {...fadeUp(0.34)}>
                <div className="relative h-[84px] w-[84px] shrink-0 overflow-hidden rounded-full">
                    <Image src="/images/nabin.jpg" alt={profile.name} fill sizes="84px" className="object-cover" priority />
                </div>
                <p className="t-small pt-1 text-body">
                    Five years in. Senior engineer at Whitehat Engineering now, where I architected that platform end to end. Before it, Sireto Technology: web, mobile, and open source in the Cardano world. Computer Engineering at Khwopa College.
                </p>
            </motion.div>

            <motion.p className="t-body mt-8 text-body" {...fadeUp(0.44)}>
                What I love is the architecture: sitting with a hard problem long enough to see its shape. It should be obvious to whoever reads it next.
            </motion.p>

            {/* A flex row, not inline text — separators glued to links with no
                whitespace make the whole chain one unbreakable word. */}
            <motion.div className="mt-9 flex flex-wrap items-center gap-x-2.5 gap-y-2" {...fadeUp(0.56)}>
                {links.map((item, index) => (
                    <span key={item.label} className="t-small flex items-center gap-2.5 text-muted">
                        {index > 0 && (
                            <span className="text-hairline" aria-hidden="true">
                                /
                            </span>
                        )}
                        <a href={item.href} {...(item.external ? { target: '_blank', rel: 'noreferrer noopener' } : {})} className="link">
                            {item.label}
                        </a>
                    </span>
                ))}
            </motion.div>
        </section>
    );
}
